import FileUpload from '@/components/FileUpload/FileUpload';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { uploadDocumentInOrganization } from '@/lib/functions/apiCalls';
import {
	handleApiError,
	handleFileRejections,
	handleGeneralError,
	handleResponse,
	handleUnexpectedError,
} from '@/lib/functions/funcUtils';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/hooks';
import {
	ApiResponse,
	ErrorResponse,
	UploadDocumentPayload,
	// UploadDocumentResponse,
} from '@/lib/types/apiTypes';
import { docName } from '@/lib/types/constants';
import { closeDialog } from '@/redux/slices/pageSlice';
import {
	transparentAnimatedStart,
	transparentAnimatedEnd,
} from '@/redux/slices/proposalSlice';
import { RootState } from '@/redux/store/store';
import { useState } from 'react';
import CategoryDropdown from '@/components/CategoryDropdown/CategoryDropdown';
import { useQueryClient } from 'react-query';

const DocumentPageDialog = () => {
	const dispatch = useAppDispatch();
	const isOpen = useAppSelector(
		(state: RootState) => state.page.dialog?.type === 'uploadDocuments'
	);
	const queryClient = useQueryClient();

	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

	const handleFiles = async (files: File[]) => {
		if (!files.length) return;
		const payload: UploadDocumentPayload = {
			file: files[0],
			name: docName,
			type: 'text',
			category: selectedCategory as string,
		};

		try {
			dispatch(transparentAnimatedStart('document'));
			const response: ApiResponse = await uploadDocumentInOrganization(payload);
			handleResponse(
				response,
				response.message === 'success'
					? 'Your document has been loaded.'
					: 'This document already exists.'
			);
			dispatch(transparentAnimatedEnd());
			queryClient.invalidateQueries('documents');
			dispatch(closeDialog());
			setSelectedCategory(null);
		} catch (error: unknown) {
			if (error instanceof Error) {
				handleGeneralError(error);
				dispatch(transparentAnimatedEnd());
			} else if (
				typeof error === 'object' &&
				error !== null &&
				'message' in error
			) {
				const apiError = error as ErrorResponse;
				handleApiError(apiError);
				dispatch(transparentAnimatedEnd());
			} else {
				handleUnexpectedError();
				dispatch(transparentAnimatedEnd());
			}
		}
	};

	return (
		<Dialog open={isOpen}>
			<DialogContent
				className="max-w-[600px] xl:max-w-[700px] 2xl:max-w-[800px] bg-white py-4 2xl:py-6"
				setModalOpen={() => {
					dispatch(closeDialog());
					setSelectedCategory(null);
				}}
			>
				<h1 className="font-bold text-left text-black capitalize dynamic-medium">
					let's get your document loaded up
				</h1>
				<div className="grid grid-cols-12 gap-3 2xl:gap-4 min-h-[450px] xl:min-h-[550px] 2xl:min-h-[620px] rounded-2xl shadow-main">
					<div className="relative flex  flex-col h-full col-span-12 bg-white rounded-[20px] 2xl:rounded-[30px]">
						<div className="flex items-center justify-start gap-3 px-4 py-2 xl:gap-4 xl:px-6 xl:py-4">
							<h2 className="font-bold text-black dynamic-medium ">
								Please select Category
							</h2>
							<CategoryDropdown setSelectedCategory={setSelectedCategory} />
						</div>
						<div className="flex flex-col items-center justify-center flex-1 gap-2 border border-b-0 border-l-0 border-r-0 border-gray-300">
							<FileUpload
								type="documentDialog"
								onDropAccepted={handleFiles}
								isUploadDisabled={!selectedCategory}
								onDropRejected={(fileRejections) =>
									handleFileRejections({ fileRejections: fileRejections })
								}
							/>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default DocumentPageDialog;
